"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RESEND_ALL_MAX = exports.resendAllSchema = exports.readdressMessageSchema = exports.messageLogQuerySchema = exports.MESSAGE_ACTION_REFUSAL_TEXT = exports.MESSAGE_ACTION_REFUSALS = exports.STALLED_FAILURE_REASON = exports.FAILURE_CAUSE_INFO = exports.FAILURE_CAUSES = exports.MESSAGE_AUDIENCES = exports.MESSAGE_DELIVERY_STATE_LABELS = exports.MESSAGE_LOG_FILTER_LABELS = exports.MESSAGE_LOG_FILTERS = exports.MESSAGE_DELIVERY_STATES = void 0;
exports.messageDeliveryState = messageDeliveryState;
exports.smtpCodeOf = smtpCodeOf;
exports.failureCauseOf = failureCauseOf;
exports.suggestEmailFix = suggestEmailFix;
exports.secretLinkAdvice = secretLinkAdvice;
exports.messageActionRefusalText = messageActionRefusalText;
exports.maskEmailAddress = maskEmailAddress;
exports.scrubEmailAddresses = scrubEmailAddresses;
exports.holdsSecretLink = holdsSecretLink;
const zod_1 = require("zod");
const calendars_1 = require("./calendars");
const schemas_1 = require("./schemas");
// ADMIN Email delivery (Admin redesign batch 2, Option A; DECISIONS-LOG 2026-11-02 Q4): what the delivery log shows,
// why an email failed in plain words, the likely-typo hint, and the rules that decide whether an email may be sent
// again. The server applies every rule here; the page only explains them.
// --- The state a message is in, as the log shows it ------------------------------------------------------------
/**
 * One state per message, worked out from its stored status:
 * - `attention`: failed, and nobody has dismissed it or sent a corrected copy (the "Needs attention" list).
 * - `dismissed`: failed, and an Admin said nobody will resend it.
 * - `replaced`: failed, and a corrected copy went to a fixed address (Fix address).
 * - `retrying`: an attempt failed and another is coming; it is still `queued` in the table.
 * - `queued`: waiting for its first attempt. `scheduled`: held until a moment ("Waiting to send").
 * - `sent`: handed to the mail server. `cancelled`: taken back before it went.
 *
 * "Delivered" and "Bounced" are not shown: nothing sets them today (no delivery or bounce receipts).
 */
exports.MESSAGE_DELIVERY_STATES = ['attention', 'dismissed', 'replaced', 'retrying', 'queued', 'scheduled', 'sent', 'cancelled'];
function messageDeliveryState(row) {
    switch (row.status) {
        case 'failed':
        case 'bounced':
            if (row.replacedById !== null)
                return 'replaced';
            return row.dismissedAt !== null ? 'dismissed' : 'attention';
        case 'queued':
            return row.attemptCount > 0 ? 'retrying' : 'queued';
        case 'scheduled':
            return 'scheduled';
        case 'sent':
        case 'delivered':
            return 'sent';
        case 'cancelled':
            return 'cancelled';
    }
}
/** The status pills, in order. `failed` covers every failure: needing attention, dismissed or replaced. */
exports.MESSAGE_LOG_FILTERS = ['attention', 'retrying', 'queued', 'scheduled', 'sent', 'failed', 'cancelled'];
exports.MESSAGE_LOG_FILTER_LABELS = {
    attention: 'Needs attention',
    retrying: 'Still trying',
    queued: 'Queued',
    scheduled: 'Waiting to send',
    sent: 'Sent',
    failed: 'Failed',
    cancelled: 'Cancelled',
};
exports.MESSAGE_DELIVERY_STATE_LABELS = {
    attention: 'Failed',
    dismissed: 'Failed · dismissed',
    replaced: 'Failed · fixed copy sent',
    retrying: 'Still trying',
    queued: 'Queued',
    scheduled: 'Waiting to send',
    sent: 'Sent',
    cancelled: 'Cancelled',
};
/** Who an email went to: a candidate (it is about a candidate or an application), or a member of staff. */
exports.MESSAGE_AUDIENCES = ['candidates', 'staff'];
// --- Why it failed --------------------------------------------------------------------------------------------
exports.FAILURE_CAUSES = [
    'mailbox_not_found',
    'domain_not_found',
    'address_rejected',
    'mailbox_full',
    'server_busy',
    'blocked',
    'cannot_connect',
    'login_refused',
    'not_written',
    'stalled',
    'other',
];
exports.FAILURE_CAUSE_INFO = {
    mailbox_not_found: { label: 'The mailbox does not exist', advice: 'Check the address for a typo, then fix it and send again.', isAddressProblem: true, isTemporary: false },
    domain_not_found: { label: "The address's domain does not exist", advice: 'The part after @ is probably mistyped. Fix the address and send again.', isAddressProblem: true, isTemporary: false },
    address_rejected: { label: "The address isn't a valid email address", advice: 'Fix the address and send again.', isAddressProblem: true, isTemporary: false },
    mailbox_full: { label: 'Their mailbox is full', advice: 'Try again later, or reach them another way.', isAddressProblem: false, isTemporary: true },
    server_busy: { label: 'The mail server was busy', advice: 'This usually clears on its own. Send it again.', isAddressProblem: false, isTemporary: true },
    blocked: { label: 'Refused as spam or by a rule at their end', advice: 'Sending again rarely helps. Reach them another way.', isAddressProblem: false, isTemporary: false },
    cannot_connect: { label: "Couldn't reach our mail server", advice: "If every email says this, the server's mail settings need checking. Send again once it is fixed.", isAddressProblem: false, isTemporary: true },
    login_refused: { label: 'Our mail server refused our sign-in', advice: "The mail password in the server's settings needs checking. Send again once it is fixed.", isAddressProblem: false, isTemporary: false },
    not_written: { label: "The email couldn't be written", advice: 'Its template is broken, so there was nothing to send. Fix the template; the next one will go.', isAddressProblem: false, isTemporary: false },
    stalled: { label: 'Sending stopped part-way', advice: 'Nothing more will be tried on its own. Send it again.', isAddressProblem: false, isTemporary: true },
    other: { label: 'Something else went wrong', advice: "The server's own words are below.", isAddressProblem: false, isTemporary: false },
};
/** `550 5.1.1`, `421`, `5.7.1`: the code the mail server gave, kept beside the plain reason. */
function smtpCodeOf(reason) {
    if (!reason)
        return null;
    const full = /\b([245]\d\d)[ -]+([245]\.\d{1,3}\.\d{1,3})\b/.exec(reason);
    if (full)
        return `${full[1]} ${full[2]}`;
    const enhanced = /\b([245]\.\d{1,3}\.\d{1,3})\b/.exec(reason);
    if (enhanced?.[1])
        return enhanced[1];
    const basic = /(?:^|[^\d.])([245][0-5]\d)(?=[ \-:]|$)/.exec(reason);
    return basic?.[1] ?? null;
}
const CAUSE_RULES = [
    ['login_refused', /\bEAUTH\b|\b535\b|5\.7\.8|invalid login|authentication (?:failed|unsuccessful|required)|username and password not accepted/i],
    ['cannot_connect', /ECONNREFUSED|ETIMEDOUT|ECONNRESET|ESOCKET|ECONNECTION|EDNS|ENOTFOUND|EAI_AGAIN|greeting never received|connection timeout|connection closed/i],
    ['domain_not_found', /5\.1\.2|5\.4\.4|domain (?:name )?(?:not found|does not exist|doesn't exist)|host (?:not found|unknown)|no mx|unroutable|unrouteable/i],
    // Before the mailbox rule: "553 5.1.3 Recipient address rejected: bad syntax" is the address's form, not a mailbox.
    ['address_rejected', /5\.1\.3|\b553\b|\b501\b|invalid (?:recipient|address|mailbox)|bad (?:recipient|destination) (?:address|mailbox)|address syntax/i],
    ['mailbox_not_found', /5\.1\.1|5\.1\.10|mailbox (?:not found|unavailable|does not exist|doesn't exist)|user unknown|unknown user|no such (?:user|mailbox)|recipient (?:address )?rejected|address (?:not found|does not exist)|does not exist/i],
    ['mailbox_full', /5\.2\.2|4\.2\.2|mailbox (?:is )?full|over quota|quota exceeded|insufficient storage/i],
    ['blocked', /5\.7\.\d|\b554\b|spam|blocked|blacklist|blocklist|policy|denied/i],
    ['server_busy', /\b4[25]\d\b|4\.\d{1,3}\.\d{1,3}|try again later|too many|rate limit|temporar|throttl|busy/i],
];
/**
 * Review W6: the reason written on an email whose tries stopped part-way — the server restarted mid-send, or the queue
 * lost its job — and that nothing would ever try again. The stalled-email sweep writes exactly this, so the cause is
 * read from it like any other reason.
 */
exports.STALLED_FAILURE_REASON = 'Sending stopped part-way: no try has run for a while and none is waiting, so nothing more would be tried.';
/**
 * The cause of a failure, read from what the mail server said (its SMTP code or words). A message that could not
 * even be written (its template broken) has no body; the caller says so, because its reason is a template error.
 */
function failureCauseOf(reason, options = {}) {
    if (options.isUnwritten === true)
        return 'not_written';
    if (!reason)
        return 'other';
    if (reason === exports.STALLED_FAILURE_REASON)
        return 'stalled';
    for (const [cause, pattern] of CAUSE_RULES)
        if (pattern.test(reason))
            return cause;
    return 'other';
}
// --- The typo hint ---------------------------------------------------------------------------------------------
/** Real providers that are one letter away from a bigger one; never "corrected". */
const KNOWN_DOMAINS = [
    'gmail.com',
    'googlemail.com',
    'yahoo.com',
    'ymail.com',
    'hotmail.com',
    'outlook.com',
    'live.com',
    'msn.com',
    'icloud.com',
    'me.com',
    'mac.com',
    'aol.com',
    'mail.com',
    'gmx.com',
    'zoho.com',
    'proton.me',
    'protonmail.com',
    'yahoo.co.uk',
    'yahoo.co.in',
];
/** The domains a typo is matched against: the big free providers people actually mistype. */
const SUGGESTED_DOMAINS = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 'live.com', 'icloud.com', 'ymail.com'];
/** Edits (insert, delete, change, swap two side by side) between two short strings. */
function editDistance(a, b) {
    const width = b.length + 1;
    const cells = new Array((a.length + 1) * width).fill(0);
    const at = (i, j) => cells[i * width + j] ?? 0;
    for (let j = 0; j <= b.length; j += 1)
        cells[j] = j;
    for (let i = 1; i <= a.length; i += 1) {
        cells[i * width] = i;
        for (let j = 1; j <= b.length; j += 1) {
            const cost = a[i - 1] === b[j - 1] ? 0 : 1;
            let best = Math.min(at(i - 1, j) + 1, at(i, j - 1) + 1, at(i - 1, j - 1) + cost);
            if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
                best = Math.min(best, at(i - 2, j - 2) + 1);
            cells[i * width + j] = best;
        }
    }
    return at(a.length, b.length);
}
/** At most this many edits from a big provider counts as a typo of it. */
const MAX_TYPO_EDITS = 2;
/**
 * `kamrul@gmial.com` → `kamrul@gmail.com`: the address with a mistyped big-provider domain corrected, or null when
 * nothing looks mistyped. Company domains are never touched: only the big free providers are suggested.
 */
function suggestEmailFix(address) {
    const trimmed = address.trim().toLowerCase();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0 || at === trimmed.length - 1)
        return null;
    const local = trimmed.slice(0, at);
    const domain = trimmed.slice(at + 1);
    if (KNOWN_DOMAINS.includes(domain))
        return null;
    let best = null;
    for (const candidate of SUGGESTED_DOMAINS) {
        const distance = editDistance(domain, candidate);
        if (distance === 0 || distance > MAX_TYPO_EDITS)
            continue;
        if (best === null || distance < best.distance)
            best = { domain: candidate, distance };
    }
    return best ? `${local}@${best.domain}` : null;
}
// --- Sending again: what is refused, and why ------------------------------------------------------------------
/**
 * Why Send again, Fix address or Dismiss is refused. The server decides; the page shows the words.
 * - `holds_secret`: it carried a sign-in or one-time link (invite, password reset, a candidate's test, booking or
 *   offer link, a scorecard link). Sending it again would hand a working link to whoever is at that address.
 * - `still_trying`: another attempt is coming; sending now could send it twice.
 * - `address_problem` and `looks_mistyped`: only "Send all again" leaves these out; fix the address first.
 */
exports.MESSAGE_ACTION_REFUSALS = [
    'not_found',
    'not_failed',
    'still_trying',
    'holds_secret',
    'anonymised',
    'nothing_to_send',
    'replaced',
    'dismissed',
    'module_off',
    'same_address',
    'address_problem',
    'looks_mistyped',
    'changed_meanwhile',
];
exports.MESSAGE_ACTION_REFUSAL_TEXT = {
    not_found: "That email isn't in the log any more.",
    not_failed: 'Only an email that failed can be sent again. This one has already gone or is on its way.',
    still_trying: "It's still being tried. Wait for its last try to finish first.",
    holds_secret: "It held a sign-in or one-time link, so it can't be sent again from here.",
    anonymised: "The person's details were anonymised, so there's nothing to send.",
    nothing_to_send: 'The email was never written, so there is nothing to send. Fix its template instead.',
    replaced: 'A corrected copy has already been sent.',
    dismissed: 'It has already been dismissed.',
    module_off: 'The part of the app that sends it is switched off.',
    same_address: "That's the address it failed at. Use Send again instead.",
    address_problem: 'The address itself is wrong. Fix it first.',
    looks_mistyped: 'The address looks mistyped. Fix it first.',
    changed_meanwhile: 'Someone else acted on it a moment ago.',
};
/** Where to go instead for an email that held a sign-in link (owner Q4: "point to People → Resend invite"). */
function secretLinkAdvice(templateKey) {
    if (templateKey === 'user.invited')
        return { text: 'Send a fresh invite from People → Resend invite.', to: '/admin/users?status=invited' };
    if (templateKey === 'auth.password_reset')
        return { text: 'Send a new link from People → Reset password.', to: '/admin/users' };
    if (templateKey === 'scorecard.link' || templateKey === 'scorecard.link_reminder')
        return { text: 'Send the interviewer a new scorecard link from the interview.', to: null };
    return { text: "Send a fresh link from the candidate's record.", to: null };
}
/** A refusal in words, with where to go instead when the email held a sign-in link (Email delivery and Background work). */
function messageActionRefusalText(refusal, templateKey) {
    const text = exports.MESSAGE_ACTION_REFUSAL_TEXT[refusal];
    return refusal === 'holds_secret' ? `${text} ${secretLinkAdvice(templateKey).text}` : text;
}
// --- Addresses in the event log (review W9) ------------------------------------------------------------------
/**
 * `kamrul@gmial.com` → `k***@gmial.com`. Events are append-only, so anonymising can never reach an address written into
 * one: the event log keeps this form and the message row (which anonymising does reach) keeps the address. The domain
 * stays, because it is usually where the mistake is.
 */
function maskEmailAddress(address) {
    const trimmed = address.trim();
    const at = trimmed.lastIndexOf('@');
    if (at <= 0)
        return '***';
    return `${trimmed.charAt(0)}***${trimmed.slice(at)}`;
}
const EMAIL_IN_TEXT = /[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;
/** A mail server's words with every address in them masked: "550 5.1.1 <kamrul@gmial.com>: no such user" → "<k***@gmial.com>". */
function scrubEmailAddresses(text) {
    return text.replace(EMAIL_IN_TEXT, (address) => maskEmailAddress(address));
}
/**
 * Whether what goes out holds a sign-in or one-time link. Every such link in the app carries `?token=` (invite,
 * password reset, a candidate's test, interview booking and offer, a scorecard link), so the rendered email is
 * checked rather than a list of templates: a person's own message with a pasted link counts too.
 */
function holdsSecretLink(...parts) {
    return parts.some((part) => typeof part === 'string' && /[?&;]token=[A-Za-z0-9%._~-]/.test(part));
}
exports.messageLogQuerySchema = zod_1.z.object({
    page: zod_1.z.coerce.number().int().positive().default(1),
    pageSize: zod_1.z.coerce.number().int().positive().max(100).default(30),
    state: zod_1.z.enum(exports.MESSAGE_LOG_FILTERS).optional(),
    q: zod_1.z.string().trim().max(120).optional(),
    audience: zod_1.z.enum(exports.MESSAGE_AUDIENCES).optional(),
    /** Asia/Dhaka days, inclusive. */
    from: calendars_1.isoDateSchema.optional(),
    to: calendars_1.isoDateSchema.optional(),
});
exports.readdressMessageSchema = zod_1.z.object({ toAddress: schemas_1.emailSchema });
/** "Send all again": every failure needing attention, or one cause's. */
exports.resendAllSchema = zod_1.z.object({ cause: zod_1.z.enum(exports.FAILURE_CAUSES).optional() });
/** At most this many go in one "Send all again"; the rest wait for the next click. */
exports.RESEND_ALL_MAX = 500;
//# sourceMappingURL=email-delivery.js.map